/**
 * The dictation commands card (docs/ux/workspace.md, "Dictation commands"):
 * the header's dictation chip is a button that opens what a doctor can say
 * and what lands in the box, read from the engine's own glossary in
 * `@beam/core` so the card cannot drift from the pass (every example there is
 * run through the real pass by a core test). A dialog on the desktop, a sheet
 * with a 44 px Close on the phone.
 */

import { SPOKEN_COMMANDS, type SpokenCommand } from '@beam/core';
import { CircleHelpIcon } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from 'cn';

type Group = { title: string; commands: SpokenCommand[] };

/** Breaks first, then lists, then punctuation: the order a doctor learns them in. */
export function commandGroups(commands: readonly SpokenCommand[] = SPOKEN_COMMANDS): Group[] {
  const isBreak = (c: SpokenCommand) => /^(new line|new paragraph)$/.test(c.say[0] ?? '');
  const isList = (c: SpokenCommand) => /^(bullet point|number one)$/.test(c.say[0] ?? '');
  return [
    { title: 'Breaks', commands: commands.filter(isBreak) },
    { title: 'Lists', commands: commands.filter(isList) },
    { title: 'Punctuation', commands: commands.filter((c) => !isBreak(c) && !isList(c)) },
  ];
}

const MODE_SENTENCE = {
  live: 'Live dictation: Deepgram in Australia, medical model, Australian English. Interim words are never saved.',
  synthetic: 'Synthetic dictation: no Deepgram key is set here, so a canned Australian dictation is replayed. The commands below work the same way.',
} as const;

function Body({ mode, phone }: { mode: 'live' | 'synthetic'; phone: boolean }) {
  return (
    <div className="flex flex-col gap-4 text-sm" data-slot="dictation-commands">
      <p className="text-beam-text-muted">{MODE_SENTENCE[mode]}</p>
      {commandGroups().map((group) => (
        <section key={group.title} aria-label={group.title} className="flex flex-col gap-2">
          <h3 className="eyebrow text-beam-text-muted">{group.title}</h3>
          <ul className="flex flex-col divide-y divide-beam-line">
            {group.commands.map((c) => (
              <li key={c.say[0]} className={cn('flex flex-col gap-1 py-2', phone ? 'min-h-11' : '')}>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="flex flex-wrap gap-1">
                    {c.say.map((s) => <span key={s} className="rounded-full bg-beam-cream-deep px-2 py-0.5 text-[13px] font-medium">{s}</span>)}
                  </span>
                  <span className="text-beam-text-soft">{c.produces}</span>
                </div>
                <p className="text-[13px] text-beam-text-muted">
                  Said: <span className="italic">{c.example.said}</span>
                </p>
                <p className="whitespace-pre-line rounded-md bg-beam-paper px-2 py-1 text-[13px] tabular">{c.example.becomes}</p>
              </li>
            ))}
          </ul>
        </section>
      ))}
      <section aria-label="Worth knowing" className="flex flex-col gap-1 text-[13px] text-beam-text-muted">
        <h3 className="eyebrow">Worth knowing</h3>
        <p>A bare "bullet" is read as a list marker unless it is followed by wound or fragment; it appears under "worth checking" in the Changes to your words list, with Undo.</p>
        <p>Format punctuates and paragraphs what you dictated as one change, never a word of it; Undo puts the dictation back as spoken. Australian spelling and the house's numbers and dates are applied as the words land.</p>
      </section>
    </div>
  );
}

export function DictationHelp({ mode, phone }: { mode: 'live' | 'synthetic'; phone: boolean }) {
  const [open, setOpen] = useState(false);
  const trigger = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      data-slot="dictation-help"
      aria-haspopup="dialog"
      aria-expanded={open}
      className={cn('gap-1.5 rounded-full text-[13px]', phone ? 'h-11 px-4' : 'h-7 px-2.5', mode === 'synthetic' && 'border-transparent bg-beam-warning-bg text-beam-warning hover:bg-beam-warning-bg')}
      onClick={() => setOpen(true)}
    >
      Dictation: {mode}
      <CircleHelpIcon aria-hidden="true" className="size-3.5" />
      <span className="sr-only">, open the dictation commands</span>
    </Button>
  );
  if (phone) {
    return (
      <>
        {trigger}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="bottom" showCloseButton={false} className="max-h-[88svh] overflow-y-auto rounded-t-[20px] px-4 shadow-beam-3">
            <SheetHeader className="px-0">
              <div className="flex items-start justify-between gap-3">
                <SheetTitle>Dictation commands</SheetTitle>
                <Button type="button" variant="ghost" size="lg" className="h-11 shrink-0 px-4" onClick={() => setOpen(false)}>Close</Button>
              </div>
              <SheetDescription>What you say, and what lands in the box.</SheetDescription>
            </SheetHeader>
            <div className="pb-6"><Body mode={mode} phone /></div>
          </SheetContent>
        </Sheet>
      </>
    );
  }
  return (
    <>
      {trigger}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>Dictation commands</DialogTitle>
            <DialogDescription>What you say, and what lands in the box. The engine does exactly this; every example is tested against it.</DialogDescription>
          </DialogHeader>
          <Body mode={mode} phone={false} />
        </DialogContent>
      </Dialog>
    </>
  );
}
